import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ModelConnection, ModelMetadata } from '@bounded/api-client';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { ErrorNotice } from '@bounded/ui/state';
import { ConfigEditor, isObject } from '@bounded/ui/config-editor';
import { Button, Command, Empty, Field, Input, Mono, Page, PageHeader, Panel, Select, Skeleton, rowClass } from '@bounded/ui/primitives';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/models')({ component: Models });

type Provider = Exclude<ModelConnection['provider'], 'hosted'>;
/** Fixed endpoints mirror packages/providers/src/types.ts checkedConnection. */
const PROVIDERS: Record<Provider, { endpoint?: string }> = {
  'openrouter-systemone': { endpoint: 'https://openrouter.ai/api/v1/systemone' },
  'openrouter-chat': { endpoint: 'https://openrouter.ai/api/v1/chat/completions' },
  'compatible-chat': {},
};
/** What a connection may be sent, as JSON pointers the server accepts. Labels are translated; the pointer is the contract. */
const CONTEXT = ['/action/raw_tool', '/action/operations', '/evidence'];
const ID = /^[a-z][a-z0-9_-]{0,95}$/;

function Models() {
  const t = useT(); const m = t.models; const f = useFormat();
  const models = useQuery(q.models());
  const hosted = useQuery(q.hostedModels());
  const [editing, setEditing] = useState<ModelMetadata | 'new' | null>(null);
  return (
    <Page>
      <PageHeader title={m.title} description={m.description} actions={!editing && <Button variant="primary" onClick={() => setEditing('new')}>{m.add}</Button>} />
      {editing && <Editor existing={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
      <Panel title={m.yours} description={m.yoursDescription}>
        {models.isPending && <div className="p-4"><Skeleton className="h-24" /></div>}
        {models.error && <div className="p-4"><ErrorNotice error={models.error} onRetry={() => void models.refetch()} /></div>}
        {models.data?.items.length === 0 && <Empty title={m.emptyTitle}>{m.emptyBody}</Empty>}
        {models.data?.items.map((x) => (
          <div key={x.id} className={`grid gap-1.5 ${rowClass}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p><span className="font-medium">{x.name}</span> <code className="text-graphite">{x.id}</code> {!x.enabled && <span className="ml-1 rounded bg-sunken px-1.5 text-sm text-graphite">{m.off}</span>}</p>
              <span className="flex items-center gap-2"><span className="text-sm text-graphite">{t.common.revision(x.revision)}</span><Button size="sm" variant="ghost" className="-mr-2.5" onClick={() => setEditing(x)}>{t.common.edit}</Button></span>
            </div>
            <p className="text-sm text-graphite">{m.summary(m.providers[x.config.provider] ?? x.config.provider, <Mono value={x.config.endpoint} head={40} tail={10} />, x.config.models.join(', '))}</p>
            <p className="text-sm text-graphite">{m.maySend(f.list(x.config.allowed_context.map((p) => m.context[p]?.[0] ?? p)))}</p>
            {x.enabled && (
              <details className="mt-1">
                <summary className="cursor-pointer text-sm text-boundary">{m.setUp}</summary>
                <p className="mt-2 text-sm text-graphite">{m.setUpBody}</p>
                <Command>{`bounded model-import --name ${x.id} --revision ${x.revision} --password-stdin`}</Command>
              </details>
            )}
          </div>
        ))}
      </Panel>
      <Panel title={m.hosted} description={m.hostedDescription}>
        {hosted.data?.items.length === 0 && <Empty title={m.noHosted} />}
        {hosted.data?.items.map((h) => (
          <div key={h.id} className={rowClass}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span><span className="font-medium">{h.config.models.join(', ')}</span> <code className="text-graphite">{h.id}</code></span>
              <span className="text-graphite">{h.enabled ? m.perDay(h.daily_call_limit, h.account_daily_limit) : m.unavailable}</span>
            </div>
            {/* Nothing to paste but the id: the device fetches the rest, and no key is involved. */}
            {h.enabled && <><p className="mt-2 text-sm text-graphite">{m.useHosted(<code>{m.idInPolicy}</code>)}</p><Command>{`bounded model-hosted --name ${h.id}`}</Command></>}
          </div>
        ))}
      </Panel>
    </Page>
  );
}

/** A personal connection as edited here: its name, whether it's on, and the key-free connection settings. The form and the JSON view edit this object. */
type Personal = { name: string; enabled: boolean; connection: Omit<ModelConnection, 'credential_id'> };
const shape = (v: unknown, notObject: string) => (isObject(v) && typeof v.name === 'string' && typeof v.enabled === 'boolean' && isObject(v.connection) && Array.isArray(v.connection.models)
  && Array.isArray(v.connection.allowed_context) && !('credential_id' in v.connection) ? null : notObject);

function Editor({ existing, onDone }: { existing: ModelMetadata | null; onDone: () => void }) {
  const t = useT(); const m = t.models;
  const queryClient = useQueryClient();
  const c = existing?.config;
  const [value, setValue] = useState<Personal>(() => ({ name: existing?.name ?? '', enabled: existing?.enabled ?? true, connection: c ? structuredClone(c) : {
    id: '', provider: 'openrouter-systemone', endpoint: PROVIDERS['openrouter-systemone'].endpoint!, models: [], allowed_context: ['/action/operations'],
    timeout_ms: 8_000, max_input_bytes: 32_768, max_output_tokens: 512 } }));
  const [jsonOk, setJsonOk] = useState(true);
  const conn = value.connection;
  const setConn = (patch: Partial<Personal['connection']>) => setValue((v) => ({ ...v, connection: { ...v.connection, ...patch } }));
  const provider = conn.provider as Provider;
  const fixedEndpoint = PROVIDERS[provider]?.endpoint;
  const connection = { ...conn, endpoint: fixedEndpoint ?? conn.endpoint, models: conn.models.map((s) => s.trim()).filter(Boolean) };
  const context = conn.allowed_context;
  const endpointChanged = !!c && connection.endpoint !== c.endpoint;
  const save = useMutation({
    mutationFn: () => api.saveModel(conn.id, { expected_revision: existing?.revision ?? 0, name: value.name.trim(), enabled: value.enabled, connection }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['models'] }); onDone(); },
  });
  const disable = useMutation({
    mutationFn: () => api.disableModel(existing!.id, existing!.revision),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['models'] }); onDone(); },
  });
  const valid = jsonOk && value.name.trim() && ID.test(conn.id) && (!existing || conn.id === existing.id) && provider in PROVIDERS && connection.models.length > 0 && context.length > 0
    && (fixedEndpoint || /^https:\/\/|^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//.test(conn.endpoint));

  return (
    <Panel title={existing ? m.editTitle(existing.name) : m.addTitle}>
      <form className="grid max-w-3xl gap-5 p-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {save.error && <ErrorNotice error={save.error} onRefresh={() => { void queryClient.invalidateQueries({ queryKey: ['models'] }); onDone(); }} />}
        <ConfigEditor id="m-json" value={value} onChange={setValue} onValidityChange={setJsonOk} validate={(v) => shape(v, t.configEditor.notObject)} rows={16}>
          <div className="grid gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={m.name} htmlFor="m-name"><Input id="m-name" required maxLength={80} value={value.name} onChange={(e) => setValue((v) => ({ ...v, name: e.target.value }))} placeholder="System One classifier" /></Field>
              <Field label={m.id} htmlFor="m-id" hint={existing ? m.idFixed : m.idHint}>
                <Input id="m-id" required className="font-mono" disabled={!!existing} value={conn.id} onChange={(e) => setConn({ id: e.target.value })} placeholder="conn_classifier" />
              </Field>
              <Field label={m.provider} htmlFor="m-provider">
                <Select id="m-provider" value={provider} onChange={(e) => { const p = e.target.value as Provider; setConn({ provider: p, endpoint: PROVIDERS[p].endpoint ?? '' }); }}>
                  {(Object.keys(PROVIDERS) as Provider[]).map((k) => <option key={k} value={k}>{m.providers[k]}</option>)}
                </Select>
              </Field>
              <Field label={m.endpoint} htmlFor="m-endpoint" hint={fixedEndpoint ? m.endpointFixed : m.endpointHint}>
                <Input id="m-endpoint" className="font-mono" disabled={!!fixedEndpoint} value={fixedEndpoint ?? conn.endpoint} onChange={(e) => setConn({ endpoint: e.target.value })} placeholder="https://llm.example.com/v1/chat/completions" />
              </Field>
              <Field label={m.modelList} htmlFor="m-models" hint={m.modelListHint}>
                <textarea id="m-models" rows={2} className="w-full rounded-[var(--radius-control)] border border-rule-strong bg-surface px-2.5 py-1.5 font-mono text-sm" value={conn.models.join('\n')} onChange={(e) => setConn({ models: e.target.value.split('\n') })} />
              </Field>
              <Field label={m.timeout} htmlFor="m-timeout" hint={m.timeoutHint}>
                <Select id="m-timeout" value={conn.timeout_ms} onChange={(e) => setConn({ timeout_ms: Number(e.target.value) })}>
                  {[...new Set([2_000, 5_000, 8_000, 15_000, 30_000, conn.timeout_ms])].sort((a, b) => a - b).map((ms) => <option key={ms} value={ms}>{m.seconds(ms / 1000)}</option>)}
                </Select>
              </Field>
            </div>
            <fieldset className="grid gap-2">
              <legend className="text-sm font-medium">{m.maySendLegend}</legend>
              {CONTEXT.map((pointer) => (
                <label key={pointer} className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1 size-4 accent-[var(--boundary)]" checked={context.includes(pointer)}
                    onChange={(e) => setConn({ allowed_context: e.target.checked ? [...context, pointer] : context.filter((p) => p !== pointer) })} />
                  <span><span>{m.context[pointer][0]}</span><span className="block text-sm text-graphite">{m.context[pointer][1]}</span></span>
                </label>
              ))}
              {context.filter((p) => !CONTEXT.includes(p)).map((p) => <p key={p} className="text-sm text-graphite">{m.alsoAllowed(<code>{p}</code>)}</p>)}
            </fieldset>
            <label className="flex items-center gap-2"><input type="checkbox" className="size-4 accent-[var(--boundary)]" checked={value.enabled} onChange={(e) => setValue((v) => ({ ...v, enabled: e.target.checked }))} />{m.enabled}</label>
          </div>
        </ConfigEditor>
        {endpointChanged && <p className="rounded-[var(--radius-control)] border border-ask/40 bg-ask/5 px-3 py-2">{m.endpointChanged}</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={!valid || save.isPending}>{existing ? m.save : m.create}</Button>
          <Button variant="ghost" onClick={onDone}>{t.common.cancel}</Button>
          {existing?.enabled && <Button variant="danger" className="ml-auto" disabled={disable.isPending} onClick={() => disable.mutate()}>{m.turnOff}</Button>}
        </div>
        {disable.error && <ErrorNotice error={disable.error} />}
      </form>
    </Panel>
  );
}
